import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { login, selectOrg, clearError, resetOrgSelection } from '../../store/authSlice';
import { userAPI } from '../../api/services';
import { OrgOption } from '../../types';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');

  // Org selection state (inline — no separate page)
  const [orgSearch, setOrgSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user, requiresOrgSelection, orgOptions } = useAppSelector(
    (state) => state.auth
  );

  const primaryOrg = orgOptions.find((o) => o.isPrimary) ?? orgOptions[0] ?? null;
  const otherOrgs = orgOptions.filter((o) => o !== primaryOrg);
  const filteredOthers = otherOrgs.filter((o) =>
    o.name.toLowerCase().includes(orgSearch.toLowerCase())
  );

  // Navigate after full authentication
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.isFirstLogin) {
        navigate('/change-password');
      } else {
        const priority = user.organizationRole?.priority ?? 0;
        const hasAdminAccess = user.role === 'admin' || priority >= 50;
        navigate(hasAdminAccess ? '/admin/dashboard' : '/employee/dashboard');
      }
    }
  }, [isAuthenticated, user, navigate]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Clear global auth error when component unmounts
  useEffect(() => {
    return () => { dispatch(clearError()); };
  }, [dispatch]);

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });

  const validateLogin = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!email.trim()) {
      newErrors.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Enter a valid email address.';
    }
    if (!password) {
      newErrors.password = 'Password is required.';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters.';
    }
    setFieldErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateLogin()) return;
    dispatch(clearError());
    dispatch(login({ email, password }));
  };

  const handleSelectOrg = (org: OrgOption) => {
    dispatch(clearError());
    dispatch(selectOrg(org._id));
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetError('');
    setResetSuccess('');
    try {
      await userAPI.requestPasswordReset(resetEmail);
      setResetSuccess('Password reset email sent! Please check your inbox.');
      setResetEmail('');
      setTimeout(() => { setShowResetModal(false); setResetSuccess(''); }, 3000);
    } catch (err: any) {
      setResetError(err.response?.data?.message || 'Failed to send password reset email.');
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetModal = () => {
    setShowResetModal(false);
    setResetEmail('');
    setResetError('');
    setResetSuccess('');
  };

  const getOrgInitials = (name: string) =>
    name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-primary-50 via-white to-brand-secondary-50 p-4">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-brand-primary-200 to-transparent rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-brand-secondary-200 to-transparent rounded-full blur-3xl opacity-20"></div>
      </div>

      <div className="relative max-w-md w-full">
        {/* Logo and Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-16 w-16 bg-gradient-to-br from-brand-primary-500 to-brand-secondary-600 rounded-2xl shadow-lg mb-4">
            <span className="text-white font-bold text-2xl">LT</span>
          </div>
          <h1 className="text-3xl font-bold text-brand-gray-900 mb-2">
            {requiresOrgSelection ? 'Choose Organisation' : 'Welcome Back!'}
          </h1>
          <p className="text-brand-gray-600">
            {requiresOrgSelection
              ? `Signed in as ${user?.name ?? user?.email}`
              : 'Sign in to Little Talent Performance Portal'}
          </p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-card-hover border border-brand-gray-200 p-8">

          {/* ── Error banner — always rendered to avoid layout shift ── */}
          <div className={`overflow-hidden transition-all duration-200 ${error ? 'max-h-24 mb-5' : 'max-h-0 mb-0'}`}>
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start">
              <svg className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm">{error}</span>
            </div>
          </div>

          {/* ── STEP 1: Credentials form ── */}
          {!requiresOrgSelection && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-brand-gray-900 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-brand-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); clearFieldError('email'); dispatch(clearError()); }}
                    placeholder="you@example.com"
                    className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-transparent transition-all ${fieldErrors.email ? 'border-red-400' : 'border-brand-gray-300'}`}
                  />
                </div>
                {fieldErrors.email && <p className="mt-1 text-xs text-red-600">{fieldErrors.email}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-brand-gray-900 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-brand-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                  </div>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); clearFieldError('password'); dispatch(clearError()); }}
                    placeholder="Enter your password"
                    className={`w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-transparent transition-all ${fieldErrors.password ? 'border-red-400' : 'border-brand-gray-300'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-brand-gray-400 hover:text-brand-gray-600"
                  >
                    {showPassword ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && <p className="mt-1 text-xs text-red-600">{fieldErrors.password}</p>}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-brand-primary-600 to-brand-secondary-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-brand-primary-700 hover:to-brand-secondary-600 disabled:from-brand-gray-300 disabled:to-brand-gray-400 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
          )}

          {/* ── STEP 2: Org selection (inline) ── */}
          {requiresOrgSelection && (
            <div className="space-y-4">
              {/* Primary org — big CTA */}
              {primaryOrg && (
                <div>
                  <p className="text-xs font-semibold text-brand-gray-500 uppercase tracking-wide mb-2">
                    Your default organisation
                  </p>
                  <button
                    onClick={() => handleSelectOrg(primaryOrg)}
                    disabled={loading}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-brand-primary-500 bg-brand-primary-50 hover:bg-brand-primary-100 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {primaryOrg.logoUrl ? (
                      <img src={primaryOrg.logoUrl} alt={primaryOrg.name} className="h-10 w-10 rounded-lg object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-brand-primary-500 to-brand-secondary-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm font-bold">{getOrgInitials(primaryOrg.name)}</span>
                      </div>
                    )}
                    <div className="text-left flex-1 min-w-0">
                      <p className="font-semibold text-brand-gray-900 truncate">{primaryOrg.name}</p>
                      <p className="text-xs text-brand-primary-600">Continue to this organisation</p>
                    </div>
                    {loading ? (
                      <svg className="animate-spin h-5 w-5 text-brand-primary-500 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-brand-primary-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                </div>
              )}

              {/* Other orgs — searchable dropdown */}
              {otherOrgs.length > 0 && (
                <div ref={dropdownRef} className="relative">
                  <p className="text-xs font-semibold text-brand-gray-500 uppercase tracking-wide mb-2">
                    Or sign in to another organisation
                  </p>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg border border-brand-gray-300 bg-white hover:border-brand-primary-400 hover:bg-brand-gray-50 transition-all text-left"
                  >
                    <span className="text-sm text-brand-gray-600">Search organisations…</span>
                    <svg
                      className={`h-4 w-4 text-brand-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-brand-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                      <div className="p-2 border-b border-brand-gray-100">
                        <div className="relative">
                          <svg className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <input
                            autoFocus
                            type="text"
                            value={orgSearch}
                            onChange={(e) => setOrgSearch(e.target.value)}
                            placeholder="Type to search…"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-brand-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary-400"
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredOthers.length === 0 ? (
                          <p className="text-sm text-brand-gray-500 text-center py-4">No organisations found</p>
                        ) : (
                          filteredOthers.map((org) => (
                            <button
                              key={org._id}
                              type="button"
                              onClick={() => { setDropdownOpen(false); handleSelectOrg(org); }}
                              disabled={loading}
                              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-brand-gray-50 transition-colors text-left disabled:opacity-50"
                            >
                              {org.logoUrl ? (
                                <img src={org.logoUrl} alt={org.name} className="h-8 w-8 rounded-md object-cover flex-shrink-0" />
                              ) : (
                                <div className="h-8 w-8 rounded-md bg-gradient-to-br from-brand-primary-400 to-brand-secondary-500 flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-xs font-bold">{getOrgInitials(org.name)}</span>
                                </div>
                              )}
                              <span className="text-sm font-medium text-brand-gray-800 truncate">{org.name}</span>
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Back link */}
              <button
                type="button"
                onClick={() => dispatch(resetOrgSelection())}
                className="w-full text-sm text-brand-gray-500 hover:text-brand-gray-700 transition-colors pt-1"
              >
                ← Sign in with a different account
              </button>
            </div>
          )}

          {/* Forgot password — only on credentials step */}
          {!requiresOrgSelection && (
            <div className="mt-6 text-center">
              <p className="text-sm text-brand-gray-500">
                Forgot your password?{' '}
                <button
                  type="button"
                  onClick={() => setShowResetModal(true)}
                  className="text-brand-primary-600 hover:text-brand-primary-800 font-semibold transition-colors"
                >
                  Reset it here
                </button>
              </p>
            </div>
          )}
        </div>

        <p className="text-center text-sm text-brand-gray-500 mt-8">
          © 2026 Little Talent. All rights reserved.
        </p>
      </div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-brand-gray-500 opacity-75" onClick={closeResetModal}></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-brand-primary-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-brand-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-3a1 1 0 011-1h2.586l4.707-4.707A6 6 0 0118 9z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-brand-gray-900">Reset Password</h3>
                  <p className="mt-2 text-sm text-brand-gray-500">Enter your email and we'll send you a temporary password.</p>

                  {resetSuccess && (
                    <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
                      <svg className="h-5 w-5 text-green-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-medium">{resetSuccess}</p>
                    </div>
                  )}
                  {resetError && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
                      <svg className="h-5 w-5 text-red-400 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <p className="text-sm font-medium">{resetError}</p>
                    </div>
                  )}

                  <form onSubmit={handlePasswordReset} className="mt-4">
                    <label htmlFor="resetEmail" className="block text-sm font-medium text-brand-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      id="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      placeholder="Enter your email address"
                      className="w-full px-3 py-2 border border-brand-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-transparent"
                    />
                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                      <button
                        type="submit"
                        disabled={resetLoading}
                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand-primary-600 text-base font-medium text-white hover:bg-brand-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {resetLoading ? (
                          <span className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Sending…
                          </span>
                        ) : 'Send Reset Email'}
                      </button>
                      <button
                        type="button"
                        onClick={closeResetModal}
                        className="mt-3 w-full inline-flex justify-center rounded-md border border-brand-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-brand-gray-700 hover:bg-brand-gray-50 focus:outline-none sm:mt-0 sm:w-auto sm:text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
